import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { SCREENS } from '@/app/screens';
import { useConnections } from '@/auth/useSession';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { setSampleActive, useSampleActive } from '@/lib/sample';

type Destination = (typeof SCREENS)[keyof typeof SCREENS]['path'];

/**
 * Turn sample data on or off for this tab (src/lib/sample.ts).
 *
 * Every switch resets the whole query cache before the next request, so no
 * screen ever shows a sample answer as the user's own, or the reverse. Each
 * switch lands on a screen with a clean URL: a Focus chosen on the sample
 * (its app ids) means nothing on the user's own account, and vice versa.
 */
export function useSampleMode() {
  const active = useSampleActive();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const set = useCallback(
    async (next: boolean, to: Destination) => {
      await queryClient.cancelQueries();
      setSampleActive(next);
      // Reset, not remove: a screen that stays mounted refetches on the new side.
      void queryClient.resetQueries();
      track(next ? ANALYTICS_EVENTS.sampleDataEntered : ANALYTICS_EVENTS.sampleDataExited);
      await navigate({ to, search: {} as never, replace: false });
    },
    [queryClient, navigate],
  );

  return {
    active,
    /** Explore the product on sample data, from Overview unless told otherwise. */
    enter: useCallback((to: Destination = SCREENS.overview.path) => set(true, to), [set]),
    /** Back to the user's own data, on the given screen. */
    exit: useCallback((to: Destination) => set(false, to), [set]),
  };
}

/**
 * Where "Connect your ad networks" leads: the welcome screen for an account
 * with no connection, else Connections. The user's own connections, which the
 * sample header never changes.
 */
export function useConnectPath(): Destination {
  const connections = useConnections();
  return connections.data?.length ? SCREENS.connections.path : SCREENS.noConnections.path;
}
